import { useState } from 'react';
import type { PhotoCard } from '@photos/shared';
import { useCreateShare } from '../../api/hooks';
import { Icon } from '../Icon';
import { Modal } from '../Modal';

type Expiry = 7 | 30 | 90;
type Allow = 'none' | 'large' | 'original';

function absoluteUrl(url: string): string {
  try {
    return new URL(url, location.origin).toString();
  } catch {
    return url;
  }
}

/** Create a share link for the selected photos (prototype `shareDialog`). */
export function ShareDialog({ photos, onClose }: { photos: Pick<PhotoCard, 'id' | 'visibility'>[]; onClose(): void }) {
  const notReleased = photos.filter((p) => p.visibility !== 'released').length;
  const [expires, setExpires] = useState<Expiry>(30);
  const [allow, setAllow] = useState<Allow>('large');
  const [copied, setCopied] = useState(false);
  const share = useCreateShare();
  const link = share.data ? absoluteUrl(share.data.url) : '';
  const n = photos.length;

  const reset = () => {
    share.reset();
    setCopied(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const input = document.getElementById('share-link') as HTMLInputElement | null;
      input?.select();
      document.execCommand?.('copy');
    }
    setCopied(true);
  };

  return (
    <Modal onClose={onClose} labelledBy="share-title">
      <h3 id="share-title">Share {n} photos</h3>
      <p className="help">
        Creates a private link anyone can open without an account. It expires automatically and you can revoke it
        anytime.
      </p>
      {notReleased ? (
        <div className="notice" style={{ color: 'var(--danger)' }}>
          <Icon name="lock" />
          <span>
            {photos.length === 1
              ? 'This photo isn’t'
              : notReleased === photos.length
                ? 'These photos aren’t'
                : `${notReleased} of these photos ${notReleased === 1 ? 'isn’t' : 'aren’t'}`}{' '}
            <b>released</b>. Share links only work for released photos.
          </span>
        </div>
      ) : (
        <>
          <div className="field">
            <label htmlFor="share-exp">Link expires</label>
            <select
              id="share-exp"
              className="inp"
              value={expires}
              onChange={(e) => {
                setExpires(Number(e.target.value) as Expiry);
                reset();
              }}
            >
              <option value={7}>In 7 days</option>
              <option value={30}>In 30 days</option>
              <option value={90}>In 90 days</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="share-dl">Visitors can download</label>
            <select
              id="share-dl"
              className="inp"
              value={allow}
              onChange={(e) => {
                setAllow(e.target.value as Allow);
                reset();
              }}
            >
              <option value="large">Large (2048px) only</option>
              <option value="original">Large + Originals</option>
              <option value="none">View only</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="share-link">Link</label>
            <div className="copy">
              <input
                id="share-link"
                className="inp"
                readOnly
                value={link}
                placeholder={share.isPending ? 'Creating link…' : 'Create the link to copy it'}
                onFocus={(e) => e.target.select()}
              />
              {link ? (
                <button type="button" className="btn" id="cp" onClick={copy}>
                  {copied ? 'Copied ✓' : 'Copy'}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn primary"
                  disabled={share.isPending}
                  onClick={() => share.mutate({ ids: photos.map((p) => p.id), expiresInDays: expires, allowDownload: allow })}
                >
                  Create link
                </button>
              )}
            </div>
            {share.error && (
              <div className="help" style={{ color: 'var(--danger)', margin: '6px 0 0' }}>
                {share.error.message}
              </div>
            )}
          </div>
        </>
      )}
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          {notReleased ? 'Close' : 'Done'}
        </button>
      </div>
    </Modal>
  );
}
